
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Heart, Share, Clock, AlertCircle, FileText } from "lucide-react";
import { Message as MessageType } from "@/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

import { PrintButton } from "@/components/ui/print-button";
import { PdfDownloadButton } from "@/components/ui/pdf-download-button";
import { EditButton } from "@/components/ui/edit-button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import StreamingText from "./chat/StreamingText";
import { ChainOfThoughtDisplay } from "./chat/ChainOfThoughtDisplay";
import DocumentTransformOverlay from "./chat/DocumentTransformOverlay";
import { useAuth } from "@/context/AuthContext";
import { createDocumentFromChat, cleanContentForEditor } from "@/services/documentTransferService";
import { useNavigate, useLocation } from "react-router-dom";

interface MessageProps {
  message: MessageType;
  isLastMessage?: boolean;
  enableStreaming?: boolean;
}

const Message: React.FC<MessageProps> = ({ 
  message, 
  isLastMessage = false,
  enableStreaming = false
}) => {
  const [liked, setLiked] = useState(false);
  const [currentContent, setCurrentContent] = useState(message.content);
  const [messageData, setMessageData] = useState(message);
  const [isTransferring, setIsTransferring] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAssistant = message.role === "assistant";
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  
  // 🎯 CHAT GERAL: Detectar contexto da rota
  const isGeneralChat = pathname.startsWith('/chat');
  
  // 🎯 CHAT GERAL: Usar flag exclusivo suggest_doc + verificar ≥ 50 linhas
  const shouldShowOverlay = useMemo(() => {
    if (overlayDismissed) return false; // 🔒 se já fechou, nunca reabrir
    if (message.role !== 'assistant') return false;
    
    // Chat Geral usa detecção rigorosa via suggest_doc E line_count
    if (isGeneralChat) {
      return message.metadata?.suggest_doc === true && (message.metadata?.line_count ?? 0) >= 50;
    }
    
    // Document Chat e outras áreas mantêm comportamento original
    return message.metadata?.isDocument === true;
  }, [overlayDismissed, isGeneralChat, message.role, message.metadata?.suggest_doc, message.metadata?.isDocument, message.metadata?.line_count]);
  
  
  // Trigger para mostrar overlay quando shouldShowOverlay + última mensagem
  useEffect(() => {
    if (overlayDismissed) return; // 🔒 não reagendar
    if (shouldShowOverlay && isLastMessage && !showOverlay) {
      delayRef.current = setTimeout(() => {
        setShowOverlay(true);
      }, 500);
    }
    return () => {
      if (delayRef.current) {
        clearTimeout(delayRef.current);
        delayRef.current = null;
      }
    };
  }, [shouldShowOverlay, isLastMessage, showOverlay, overlayDismissed]);
  
  const handleShare = () => {
    try {
      if (navigator.share) {
        navigator.share({
          title: "Conversa com Justiçai Legal",
          text: currentContent,
        }).catch((error) => {
          console.log("Erro ao compartilhar:", error);
          copyToClipboard();
        });
      } else {
        copyToClipboard();
      }
    } catch (error) {
      console.error("Error sharing content:", error);
      copyToClipboard();
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentContent);
    toast({
      title: "Texto copiado",
      description: "Conteúdo copiado para a área de transferência",
      duration: 3000,
    });
  };

  const handleLike = () => {
    setLiked(!liked);
    if (!liked) {
      toast({
        title: "Obrigado pelo feedback!",
        description: "Seu feedback nos ajuda a melhorar.",
        duration: 3000,
      });
    }
  };

  const handleContentUpdated = (newContent: string) => {
    setCurrentContent(newContent);
    setMessageData({
      ...messageData,
      content: newContent,
      edited: true,
      edited_at: new Date().toISOString()
    });
  };

  const handleTransformToDocument = async () => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para criar documentos.",
        variant: "destructive",
      });
      return;
    }

    setIsTransferring(true);
    setShowOverlay(false); // Esconder overlay ao iniciar transformação

    try {
      const cleanedContent = cleanContentForEditor(currentContent);
      const documentId = await createDocumentFromChat(cleanedContent, user.id);

      if (documentId) {
        toast({
          title: "Documento criado!",
          description: "O conteúdo foi transferido para a área de documentos.",
          duration: 4000,
        });

        // Navegar para o documento criado
        navigate(`/documentos/${documentId}`);
      } else {
        throw new Error('Falha ao criar documento');
      }
    } catch (error) {
      console.error('Erro ao transferir para documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o documento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  const handleStreamingComplete = () => {
    // Mostrar overlay automaticamente quando streaming completa E shouldShowOverlay é true
    if (shouldShowOverlay && isLastMessage && !overlayDismissed) {
      setShowOverlay(true);
    }
  };

  const handleDismissOverlay = () => {
    if (delayRef.current) { // 🧹 cancela timer pendente
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
    setOverlayDismissed(true);
    setShowOverlay(false);
  };

  const formatMessageDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "HH:mm, dd 'de' MMMM", { locale: ptBR });
  };

  // ✅ CORREÇÃO: Não renderizar mensagens vazias da IA
  if (isAssistant && !currentContent?.trim()) {
    return null;
  }

  return (
    <div className={cn(
      "flex w-full max-w-4xl mx-auto px-2 md:px-4",
      isAssistant ? "justify-start" : "justify-end",
      isLastMessage && "animate-fade-up"
    )}>
      <div 
        className={cn(
          "rounded-xl px-3 py-2 md:px-4 md:py-3 max-w-[95%] sm:max-w-[90%] md:max-w-[85%]",
          "break-words overflow-wrap-anywhere",
          isAssistant 
            ? "bg-secondary text-secondary-foreground rounded-tl-none" 
            : "bg-primary text-primary-foreground rounded-tr-none"
        )}
        data-message-id={isAssistant ? message.id : undefined}
      >
        {isAssistant ? (
          <div className="relative">
            {/* ⚖️ CORREÇÃO CIRÚRGICA: Indicador "Pensando..." quando streaming sem conteúdo */}
            {message.metadata?.isStreaming && !(currentContent?.trim()) && (
              <div className="text-xs text-muted-foreground flex items-center gap-2 mb-2">
                <span className="animate-pulse">● ● ●</span> Pensando...
              </div>
            )}

            {/* Wrapper com classe ABNT aplicada condicionalmente */}
            {(currentContent?.trim()) && (
              <div className={cn(
                "prose prose-sm dark:prose-invert max-w-none break-words overflow-wrap-anywhere",
                // ⚖️ MUDANÇA CIRÚRGICA: Aplicar formatação ABNT quando metadata indicar
                message.metadata?.template === 'abnt_juridico' && "abnt-format chat-abnt"
              )}>
                <StreamingText
                  content={currentContent}
                  enableStreaming={enableStreaming && isLastMessage && !message.metadata?.isStreaming}
                  speed={12000}
                  onStreamingComplete={handleStreamingComplete}
                  hideSkipButton={message.metadata?.isStreaming} // Esconder botão skip durante streaming real
                />
                
                {/* Indicador de streaming real-time */}
                {message.metadata?.isStreaming && (
                  <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1" />
                )}
              </div>
            )}
            
            {/* Document Transform Overlay */}
            <DocumentTransformOverlay
              isVisible={showOverlay}
              onTransform={handleTransformToDocument}
              onDismiss={handleDismissOverlay}
              isTransferring={isTransferring}
            />
            
            {/* Chain of Thought Display */}
            {message.metadata?.reasoning_steps && (
              <div className="mt-4">
                <ChainOfThoughtDisplay
                  steps={message.metadata.reasoning_steps}
                  confidenceScore={message.metadata.confidence_score}
                  sourcesUsed={message.metadata.sources_used}
                  visible={true}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none break-words text-sm md:text-base">
            {currentContent}
          </div>
        )}
        
        {/* Indicador de mensagem editada */}
        {isAssistant && messageData.edited && (
          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
            <AlertCircle className="h-3 w-3" />
            <span>Editado</span>
            {messageData.edited_at && (
              <span>em {formatMessageDate(messageData.edited_at)}</span>
            )}
          </div>
        )}
        
        {/* ⚖️ CORREÇÃO CIRÚRGICA: Ações só quando tem conteúdo */}
        {isAssistant && (currentContent?.trim()) && (
          <TooltipProvider>
            <div className="flex justify-end gap-1 md:gap-2 mt-1 md:mt-2 pt-1 border-t border-secondary-foreground/10">
              <div className="flex items-center gap-1 mr-auto text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{formatMessageDate(message.created_at)}</span>
              </div>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleLike}
                    aria-label="Curtir"
                  >
                    <Heart className={cn(
                      "h-3 w-3 md:h-4 md:w-4",
                      liked ? "fill-red-500 text-red-500" : "text-secondary-foreground/70"
                    )} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Curtir esta resposta</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleShare}
                    aria-label="Compartilhar"
                  >
                    <Share className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Compartilhar ou copiar texto</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Botão Transformar em Documento - sempre visível */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleTransformToDocument}
                    disabled={isTransferring}
                    aria-label="Transformar em documento"
                  >
                    <FileText className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Transformar em documento</p>
                </TooltipContent>
              </Tooltip>

              <EditButton 
                messageId={message.id}
                currentContent={currentContent}
                onContentUpdated={handleContentUpdated}
              />
              <PdfDownloadButton 
                content={currentContent} 
                messageId={message.id} 
              />
              <PrintButton 
                content={currentContent} 
                messageId={message.id} 
              />
            </div>
          </TooltipProvider>
        )}

        {!isAssistant && (
          <div className="flex items-center gap-1 mt-1 justify-end text-xs text-white/70">
            <Clock className="h-3 w-3" />
            <span>{formatMessageDate(message.created_at)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
